import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import type { Movie, NewsItem } from "@/types/content";

export function getMovieById(id?: string): Movie | undefined {
  if (!id) return undefined;
  return movies.find((item) => item.id === id);
}

export function getNewsById(id?: string): NewsItem | undefined {
  if (!id) return undefined;
  return news.find((item) => item.id === id);
}

export function getGenres(): string[] {
  return Array.from(new Set(movies.map((item) => item.genre)));
}

export const yearRanges = [
  { value: "2024-2025", label: "2024 - 2025", min: 2024, max: 2025 },
  { value: "2020-2023", label: "2020 - 2023", min: 2020, max: 2023 },
  { value: "2015-2019", label: "2015 - 2019", min: 2015, max: 2019 },
  { value: "2009-2014", label: "2009 - 2014", min: 2009, max: 2014 },
];

export const ratingOptions = [
  { value: "9", label: "9.0 分以上", min: 9 },
  { value: "8.5", label: "8.5 分以上", min: 8.5 },
  { value: "8", label: "8.0 分以上", min: 8 },
  { value: "7.5", label: "7.5 分以上", min: 7.5 },
];

export function normalize(text: string): string {
  return text.trim().toLowerCase();
}

export function searchMovies(keyword: string): Movie[] {
  const q = normalize(keyword);
  if (!q) return [];
  return movies.filter((movie) => {
    const haystack = [movie.title, movie.genre, movie.summary, movie.plot, ...movie.tags].join(" ");
    return normalize(haystack).includes(q);
  });
}

export function searchNews(keyword: string): NewsItem[] {
  const q = normalize(keyword);
  if (!q) return [];
  return news.filter((item) => {
    const haystack = [item.title, item.category, item.excerpt, ...item.tags].join(" ");
    return normalize(haystack).includes(q);
  });
}

export function getRelatedMovies(movie: Movie, limit = 3): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const sharedTags = item.tags.filter((tag) => movie.tags.includes(tag)).length;
      const sameGenre = item.genre === movie.genre ? 2 : 0;
      return { item, score: sharedTags * 2 + sameGenre + item.rating / 10 };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getRelatedNews(item: NewsItem, limit = 3): NewsItem[] {
  const scored = news
    .filter((entry) => entry.id !== item.id)
    .map((entry) => {
      const sharedTags = entry.tags.filter((tag) => item.tags.includes(tag)).length;
      const sameCategory = entry.category === item.category ? 2 : 0;
      return { entry, score: sharedTags * 2 + sameCategory };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.entry);
}

export function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${year} 年 ${Number(month)} 月 ${Number(day)} 日`;
}

export function formatShortDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${year}.${month}.${day}`;
}

export function filterMovies(options: {
  keyword?: string;
  genre?: string;
  year?: string;
  rating?: string;
  sort?: string;
}): Movie[] {
  const { keyword = "", genre = "", year = "", rating = "", sort = "updated" } = options;
  const q = normalize(keyword);
  const yearRange = yearRanges.find((entry) => entry.value === year);
  const ratingOption = ratingOptions.find((entry) => entry.value === rating);

  const result = movies.filter((movie) => {
    const haystack = [movie.title, movie.summary, movie.genre, ...movie.tags, ...movie.cast, movie.director].join(" ");
    const matchKeyword = q ? normalize(haystack).includes(q) : true;
    const matchGenre = genre ? movie.genre === genre : true;
    const matchYear = yearRange ? movie.year >= yearRange.min && movie.year <= yearRange.max : true;
    const matchRating = ratingOption ? movie.rating >= ratingOption.min : true;
    return matchKeyword && matchGenre && matchYear && matchRating;
  });

  return result.sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "year") return b.year - a.year;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
}